import { useEffect, useMemo, useRef } from "react";
import maplibregl, { type LngLat, type Map as MlMap, type Marker as MlMarker } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { useUnistyles } from "react-native-unistyles";
import { View } from "@src/components";
import type { ScenarioConfigItem } from "../hooks/use-scenario-configs";

/**
 * Minimal shape the canvas needs to draw + label a scenario's pin. Both the
 * grid page's `ScenarioRow` and the config page's `ConfigScenario` satisfy it
 * structurally (they carry `cameraDisplayName` / `assetCameraLabel` under the
 * same field name — see the config page's adapter), so the canvas stays
 * decoupled from either page's row model.
 */
export interface MapMarkerRow {
  scenarioUuid: string;
  scenarioName: string;
  cameraDisplayName: string;
}

/**
 * A located asset shown on the map as a context marker (distinct from the
 * clickable scenario pins). Rendered on the config page so the operator can see
 * where the physical unit is before placing a scenario pin. Assets with no
 * location are simply omitted by the caller (no placeholder position).
 */
export interface AssetMarker {
  id: string;
  label: string;
  coordinate: { lat: number; lng: number };
}

interface MapCanvasProps {
  rows: MapMarkerRow[];
  configByUuid: Record<string, ScenarioConfigItem>;
  tenantId?: string;
  selectedScenarioUuid?: string | null;
  onScenarioSelect: (scenarioUuid: string) => void;
  onMapClick?: (lngLat: LngLat) => void;
  /**
   * Context markers for the site's located assets (config page). Purely
   * informational — not clickable, visually distinct from scenario pins.
   */
  assetMarkers?: AssetMarker[];
  /**
   * Asset id belonging to the currently-selected scenario. That asset's marker
   * is drawn in purple (the "this is the one you're placing" landmark); every
   * other located asset on the site recedes to the same neutral grey as the
   * non-selected scenario pins, so the map isn't a sea of identical purple.
   */
  selectedAssetId?: string | null;
  /**
   * When supplied, the initial + auto-fit viewport is computed from THESE
   * coordinates instead of the scenario pins. The config page uses this to
   * implement its asset-first centring chain (fit located assets → else
   * configured pins → else AU/NZ), which the pin-only default can't express.
   * Omit to keep the grid page's pin-based centring unchanged.
   */
  focusCoordinates?: { lat: number; lng: number }[];
  /**
   * Draft pin shown immediately while the operator is configuring a scenario,
   * before the change has been saved. Decoupled from `configByUuid` so the
   * pin appears the moment the user clicks the map. The arrow points along
   * `direction` (compass degrees, 0 = N) and can be dragged to rotate when
   * `onDraftDirectionChange` is supplied. Omit `direction` to render the
   * pin without an arrow (used when the operator has explicitly cleared
   * the bearing input).
   */
  draftPin?: { coordinate: { lat: number; lng: number }; direction?: number } | null;
  onDraftDirectionChange?: (degrees: number) => void;
  /**
   * Scenario uuid whose persisted marker should be hidden while the operator
   * is editing it — the in-flight `draftPin` represents the same scenario, so
   * showing both at once produces two markers (a stale saved one and a live
   * draft) until save. Callers should only pass the editing uuid when a
   * `draftPin` is actually being rendered; otherwise the persisted marker
   * stays visible (e.g. while the operator is mid-edit and the lat/lng
   * inputs are temporarily blank or invalid).
   */
  suppressedScenarioUuid?: string | null;
  height?: number;
}

/**
 * Australia bounding box. Default view when there are no pins yet.
 */
const AUSTRALIA_BBOX: [number, number, number, number] = [112.0, -44.0, 154.0, -10.0];

/**
 * Default fallback fit. Used by the init effect (no initial pins) and the
 * auto-fit effect when the active tenant has zero pins (e.g. tenant switch
 * from a region with pins to one without).
 */
type Coord = { lat: number; lng: number };

function fitDefaultBounds(map: MlMap, animated: boolean) {
  map.fitBounds(AUSTRALIA_BBOX, { padding: 40, ...(animated ? { duration: 800 } : {}) });
}

/**
 * Compute the right viewport for a set of pins:
 *   - 0 pins → default Australia bbox
 *   - 1 pin  → centre + street-scale zoom (degenerate bbox would zoom to ~22)
 *   - 2+ pins → fit-bounds with maxZoom guard so two close pins don't snap to a single street
 *
 * Returns either a `flyTo` (animated) or a `MapOptions` partial (consumed by
 * the constructor) depending on `mode`. The init effect uses constructor
 * options because the map isn't built yet; the auto-fit effect uses flyTo /
 * fitBounds to animate gently.
 */
function applyPinViewport(map: MlMap | null, pins: Coord[], mode: "animate" | "default") {
  if (!map) return;
  if (pins.length === 0) {
    if (mode === "animate") fitDefaultBounds(map, true);
    return;
  }
  if (pins.length === 1) {
    map.flyTo({ center: [pins[0].lng, pins[0].lat], zoom: 13, duration: mode === "animate" ? 800 : 0 });
    return;
  }
  const lats = pins.map((p) => p.lat);
  const lngs = pins.map((p) => p.lng);
  map.fitBounds([Math.min(...lngs), Math.min(...lats), Math.max(...lngs), Math.max(...lats)], {
    padding: 60,
    maxZoom: 14,
    duration: mode === "animate" ? 800 : 0,
  });
}

function makeMarkerElement(colour: string): HTMLDivElement {
  const el = document.createElement("div");
  // MapLibre positions custom marker elements purely via a `transform` and
  // assumes the element establishes its own positioning context from the
  // canvas-container's origin. Its built-in marker element is
  // `position: absolute`; a `relative` element instead keeps a normal-flow
  // origin, so the transform lands it offset by that flow position (this was
  // the ~36px vertical drift on click-to-place). `absolute` also still acts
  // as the containing block for the arrow SVG child, which is what we need.
  el.style.position = "absolute";
  el.style.top = "0";
  el.style.left = "0";
  el.style.width = "18px";
  el.style.height = "18px";
  el.style.cursor = "pointer";
  const dot = document.createElement("div");
  // border-box so the 2px white border is drawn *inside* the 18×18 footprint.
  // With the default content-box, `width/height: 100%` (18px) plus the border
  // renders a 22px box that grows down-and-right from the element's top-left,
  // shifting the dot's visual centre ~2px off the marker's anchor point (the
  // click point / arrow base). border-box keeps the dot centred on the anchor.
  dot.style.boxSizing = "border-box";
  dot.style.width = "100%";
  dot.style.height = "100%";
  dot.style.borderRadius = "50%";
  dot.style.border = "2px solid white";
  dot.style.background = colour;
  dot.style.boxShadow = "0 1px 4px rgba(0,0,0,0.4)";
  el.appendChild(dot);
  return el;
}

/**
 * Asset context marker: a camera (camcorder) silhouette — deliberately a
 * different shape from the round scenario pins so the operator can tell "this
 * is where the unit is" apart from "this is a placed scenario". Not clickable
 * — no cursor, no pointer handlers — so map clicks pass through for pin
 * placement.
 *
 * Built as an inline SVG (body = rounded rect, lens = triangle on the right,
 * matching a standard camcorder glyph) filled in the asset `colour`. A white
 * stroke + drop-shadow keep it legible over dark satellite imagery. The 24×24
 * element is anchored at its centre by MapLibre (`translate(-50%,-50%)`), so
 * the SVG fills the element and its centre sits on the asset coordinate.
 */
function makeAssetMarkerElement(colour: string): HTMLDivElement {
  const el = document.createElement("div");
  // Same positioning contract as makeMarkerElement: MapLibre only writes a
  // transform, so the element must anchor at the canvas-container origin
  // (absolute/top:0/left:0) or it drifts by its normal-flow offset.
  el.style.position = "absolute";
  el.style.top = "0";
  el.style.left = "0";
  el.style.width = "24px";
  el.style.height = "24px";
  el.style.pointerEvents = "none";

  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", "24");
  svg.setAttribute("height", "24");
  svg.style.display = "block";
  svg.style.filter = "drop-shadow(0 1px 3px rgba(0,0,0,0.5))";

  // Camcorder glyph: a rounded-rect body on the left and a triangular lens
  // barrel on the right, authored to sit centred in the 24×24 box. A white
  // outline (via a slightly larger underlay of the same paths) lifts it off
  // busy imagery without a separate stroke pass per shape.
  const body = document.createElementNS(ns, "rect");
  body.setAttribute("class", "tf-asset-fill");
  body.setAttribute("x", "3");
  body.setAttribute("y", "7");
  body.setAttribute("width", "13");
  body.setAttribute("height", "10");
  body.setAttribute("rx", "2.5");
  body.setAttribute("fill", colour);
  body.setAttribute("stroke", "rgba(255,255,255,0.95)");
  body.setAttribute("stroke-width", "1.5");

  const lens = document.createElementNS(ns, "path");
  // Triangle: base flush with the body's right edge, apex pointing right.
  lens.setAttribute("class", "tf-asset-fill");
  lens.setAttribute("d", "M16 10 L21 7 L21 17 L16 14 Z");
  lens.setAttribute("fill", colour);
  lens.setAttribute("stroke", "rgba(255,255,255,0.95)");
  lens.setAttribute("stroke-width", "1.5");
  lens.setAttribute("stroke-linejoin", "round");

  svg.appendChild(lens);
  svg.appendChild(body);
  el.appendChild(svg);
  return el;
}

/**
 * Recolours an existing asset marker element in place (fill only; the white
 * stroke + shadow are unchanged). Used when the selection changes so we can
 * flip an asset between the selected purple and the non-selected grey without
 * tearing down and rebuilding the marker.
 */
function setAssetMarkerColour(el: HTMLElement, colour: string): void {
  el.querySelectorAll(".tf-asset-fill").forEach((shape) => shape.setAttribute("fill", colour));
}

/**
 * Adds (or refreshes) the small SVG arrow that points along `direction`
 * (compass degrees, 0 = N, clockwise). The arrow is a child of the marker
 * element rotated around the marker centre. When `direction` is `undefined`,
 * any existing arrow is removed.
 *
 * The arrow's SVG is a 120×120 box with its geometric centre nailed to the
 * dot's centre (via `left/top: 50%` + negative half-size margins). The
 * artwork lives around the SVG origin in a `-60 -60 120 120` viewBox, so
 * `transform-origin: center` pivots the artwork around the dot. We don't
 * use a 0×0 SVG here because per CSS-SVG a zero-area viewport disables
 * rendering of the entire SVG. Pointer-events are off by default; draft
 * markers turn them on for the rotation handle and visible artwork.
 */
const ARROW_CLASS = "tf-marker-arrow";
function syncArrow(el: HTMLDivElement, direction: number | undefined, draggable: boolean): SVGSVGElement | null {
  const existing = el.querySelector<SVGSVGElement>(`.${ARROW_CLASS}`);
  // Two independent axes here:
  //   - `direction`: whether there's a committed bearing (→ show the arrow
  //     shaft + head, rotated to point that way)
  //   - `draggable`: whether the operator can set/change the bearing (→ the
  //     grab handle must exist so they have something to drag)
  // A fresh pin is draggable but has no bearing yet. Previously we removed the
  // whole SVG when `direction === undefined`, which deleted the handle too and
  // left the operator with nothing to grab (and no discoverable way to set a
  // direction on the map). Now we keep the SVG whenever it's draggable and
  // render a neutral "unset" handle; only a non-draggable, direction-less
  // marker removes the arrow entirely.
  if (direction === undefined && !draggable) {
    existing?.remove();
    return null;
  }
  const svg = existing ?? createArrowSvg();
  if (!existing) el.appendChild(svg);

  const hasBearing = direction !== undefined;
  // CSS rotate uses clockwise + screen coordinates, which matches a compass
  // bearing where 0 = up = north, so we apply the bearing directly. The SVG is
  // centred on the dot (120×120 box, geometric centre nailed to the dot via
  // `left/top: 50%` + negative margins) and `transform-origin: center` pivots
  // around that centre, so the arrow rotates in place.
  svg.style.transformOrigin = "center";
  svg.style.transform = `rotate(${direction ?? 0}deg)`;

  // Shaft + head styling depends on whether there's a committed bearing:
  //   - committed: solid white arrow (shaft + head), invisible tip handle
  //   - unset (but draggable): a muted, dashed shaft with NO head, plus a
  //     visible grab dot at the tip — reads as "swing me to set a direction"
  //     without implying a saved north bearing (the save still sends null).
  const shaft = svg.querySelector<SVGLineElement>(".tf-marker-arrow-shaft");
  const head = svg.querySelector<SVGPolygonElement>(".tf-marker-arrow-head");
  if (head) head.style.display = hasBearing ? "" : "none";
  if (shaft) {
    shaft.style.display = "";
    if (hasBearing) {
      shaft.setAttribute("stroke", "white");
      shaft.setAttribute("stroke-dasharray", "");
      shaft.style.opacity = "1";
    } else {
      // Muted dashed shaft to signal "unset — drag to set".
      shaft.setAttribute("stroke", "white");
      shaft.setAttribute("stroke-dasharray", "3 4");
      shaft.style.opacity = "0.6";
    }
  }

  const handle = svg.querySelector<SVGCircleElement>(".tf-marker-arrow-handle");
  if (handle) {
    handle.style.cursor = draggable ? "grab" : "default";
    handle.style.pointerEvents = draggable ? "auto" : "none";
    // Neutral handle: when there's no bearing, make the grab point visible (a
    // small white disc with a subtle ring) so the operator can see there's
    // something to drag. Once a bearing exists the handle returns to being an
    // invisible hit-target sitting on the arrow tip.
    if (hasBearing) {
      handle.setAttribute("fill", "rgba(255,255,255,0.001)");
      handle.setAttribute("stroke", "rgba(0,0,0,0.0)");
      handle.setAttribute("stroke-width", "0");
      handle.setAttribute("r", "14");
    } else if (draggable) {
      handle.setAttribute("fill", "rgba(255,255,255,0.9)");
      handle.setAttribute("stroke", "rgba(0,0,0,0.4)");
      handle.setAttribute("stroke-width", "2");
      handle.setAttribute("r", "7");
    }
  }
  // Per-child pointer-events. The root SVG must stay `pointer-events: none`
  // even when draggable — the 120×120 box is mostly empty (the arrow sits in
  // the upper half), so making the root hit-testable would swallow map clicks
  // anywhere in the lower half and to the sides of the dot, breaking
  // click-to-place into that footprint. Each visible child opts in
  // individually. When not draggable, every child stays `none` so the dot's
  // marker click — which lives on the parent element, not the SVG — keeps
  // working.
  svg.style.pointerEvents = "none";
  const blocker = svg.querySelector<SVGRectElement>(".tf-marker-arrow-blocker");
  if (blocker) blocker.style.pointerEvents = draggable ? "auto" : "none";
  if (shaft) shaft.style.pointerEvents = draggable && hasBearing ? "stroke" : "none";
  if (head) head.style.pointerEvents = draggable && hasBearing ? "fill" : "none";
  return svg;
}

function createArrowSvg(): SVGSVGElement {
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.setAttribute("class", ARROW_CLASS);
  // 120×120 px SVG with origin (0, 0) at the geometric centre via the
  // `-60 -60 120 120` viewBox. The artwork is authored around (0, 0) so
  // shaft/head/handle coordinates read like compass-relative offsets
  // ("40 px above the dot" = `y="-40"`).
  //
  // We previously tried a 0×0 SVG with `overflow: visible`. Per CSS-SVG
  // spec, an SVG with zero width or height has a zero-area viewport and
  // browsers suppress its rendering entirely — which is why the arrow
  // disappeared. Giving the SVG real dimensions plus a centred viewBox
  // is both spec-compliant and keeps the rotation pivot on the dot.
  const SIZE = 120;
  const HALF = SIZE / 2;
  svg.setAttribute("viewBox", `${-HALF} ${-HALF} ${SIZE} ${SIZE}`);
  svg.setAttribute("width", String(SIZE));
  svg.setAttribute("height", String(SIZE));
  svg.style.position = "absolute";
  // Anchor the SVG's geometric centre on the dot's centre. We can't use
  // `translate(-50%, -50%)` here because `syncArrow` writes the rotation
  // into the same `transform` property — combining them would either
  // overwrite each other or force every rotation to repeat the offset.
  // Plain margins keep the two concerns separate.
  svg.style.left = "50%";
  svg.style.top = "50%";
  svg.style.marginLeft = `${-HALF}px`;
  svg.style.marginTop = `${-HALF}px`;
  svg.style.pointerEvents = "none";

  // Invisible click-blocker capsule covering the entire visible-arrow band
  // (from a few pixels above the dot to past the arrow head, with enough
  // width to cover the head triangle). Matches the click-target of the
  // visible artwork so a slightly-off-axis click still gets swallowed by the
  // marker rather than reaching the maplibre canvas. `syncArrow` flips
  // `pointer-events` on this only when the arrow is draggable.
  const blocker = document.createElementNS(ns, "rect");
  blocker.setAttribute("class", "tf-marker-arrow-blocker");
  blocker.setAttribute("x", "-10");
  blocker.setAttribute("y", "-58");
  blocker.setAttribute("width", "20");
  blocker.setAttribute("height", "60");
  blocker.setAttribute("fill", "transparent");
  blocker.style.pointerEvents = "none";
  svg.appendChild(blocker);

  const shaft = document.createElementNS(ns, "line");
  shaft.setAttribute("class", "tf-marker-arrow-shaft");
  shaft.setAttribute("x1", "0");
  shaft.setAttribute("y1", "0");
  shaft.setAttribute("x2", "0");
  shaft.setAttribute("y2", "-40");
  shaft.setAttribute("stroke", "white");
  shaft.setAttribute("stroke-width", "4");
  shaft.setAttribute("stroke-linecap", "round");
  shaft.style.filter = "drop-shadow(0 1px 2px rgba(0,0,0,0.4))";
  shaft.style.pointerEvents = "none";
  svg.appendChild(shaft);

  const head = document.createElementNS(ns, "polygon");
  head.setAttribute("class", "tf-marker-arrow-head");
  head.setAttribute("points", "-8,-36 0,-52 8,-36");
  head.setAttribute("fill", "white");
  head.style.filter = "drop-shadow(0 1px 2px rgba(0,0,0,0.4))";
  head.style.pointerEvents = "none";
  svg.appendChild(head);

  // Hit-target only — visually invisible but ~14 px radius so a touch
  // input can grab it. `pointerEvents` is flipped to `auto` in `syncArrow`
  // when the arrow is draggable; otherwise it must stay `none` so map
  // clicks fall through to the click-to-place handler.
  const handle = document.createElementNS(ns, "circle");
  handle.setAttribute("class", "tf-marker-arrow-handle");
  handle.setAttribute("cx", "0");
  handle.setAttribute("cy", "-44");
  handle.setAttribute("r", "14");
  handle.setAttribute("fill", "rgba(255,255,255,0.001)");
  handle.setAttribute("stroke", "rgba(0,0,0,0.0)");
  handle.style.pointerEvents = "none";
  svg.appendChild(handle);

  return svg;
}

function makeDraftMarkerElement(colour: string): HTMLDivElement {
  return makeMarkerElement(colour);
}

const STYLE_URL = "https://api.maptiler.com/maps/hybrid/style.json?key=get_your_own_OpIi9ZULNHzrESv6T2vL";
/**
 * Pure-OSM raster fallback when STYLE_URL fails (e.g. demo key exhausted).
 * No satellite imagery, but always renders.
 */
const FALLBACK_STYLE = {
  version: 8 as const,
  sources: {
    osm: {
      type: "raster" as const,
      tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
      tileSize: 256,
      attribution: "© OpenStreetMap contributors",
    },
  },
  layers: [{ id: "osm", type: "raster" as const, source: "osm" }],
};

/**
 * Shared MapLibre canvas used by both the Grid view (read-only strip) and the
 * Setup view (with click-to-place editing). Owns the map instance, marker
 * lifecycle, and tile fallback. The Setup-only chrome (un-positioned list,
 * lat/lng inputs, save/cancel) lives in ScenarioMap.web.tsx.
 */
export function MapCanvas({
  rows,
  configByUuid,
  tenantId = "",
  selectedScenarioUuid,
  onScenarioSelect,
  onMapClick,
  assetMarkers,
  selectedAssetId,
  focusCoordinates,
  draftPin,
  onDraftDirectionChange,
  suppressedScenarioUuid,
  height,
}: MapCanvasProps) {
  const { theme } = useUnistyles();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MlMap | null>(null);
  const markersRef = useRef<Map<string, MlMarker>>(new Map());
  const markerClickHandlersRef = useRef<Map<string, (ev: MouseEvent) => void>>(new Map());
  const assetMarkersRef = useRef<Map<string, MlMarker>>(new Map());
  const draftMarkerRef = useRef<MlMarker | null>(null);
  // Held in a ref because the pointermove handler is bound once per drag and
  // needs the latest callback identity without re-creating the listener.
  const onDraftDirectionChangeRef = useRef(onDraftDirectionChange);
  onDraftDirectionChangeRef.current = onDraftDirectionChange;
  // The pointermove handler reads the *current* draft coordinate to project
  // the marker centre. Holding it in a ref lets the bound handler stay alive
  // across draft updates (which fire on every tick of the drag) without
  // re-binding listeners and dropping the in-progress drag.
  const draftPinRef = useRef(draftPin);
  draftPinRef.current = draftPin;

  // Timestamp of the last arrow-drag release. MapLibre synthesises a `click`
  // on pointer-up, and because the drag handle is an SVG child of the marker
  // (not a separate map layer we can `stopPropagation` cleanly against), that
  // click would otherwise reach the click-to-place handler and re-drop the pin
  // at the release point. The click handler ignores clicks that land within a
  // short window after a drag ends.
  const lastArrowDragEndRef = useRef(0);

  // Computed once per render so the init effect, the auto-fit effect, and
  // the marker-sync effect all see the same list. Identity stays stable
  // when rows/configByUuid don't change, so the auto-fit effect doesn't
  // re-fire spuriously.
  const pins: Coord[] = useMemo(
    () =>
      rows
        .filter((r) => r.scenarioUuid !== suppressedScenarioUuid)
        .map((r) => configByUuid[r.scenarioUuid]?.coordinate)
        .filter((c): c is Coord => !!c),
    [rows, configByUuid, suppressedScenarioUuid],
  );

  // What the viewport fits to. The grid page fits its scenario pins (default);
  // the config page overrides with `focusCoordinates` to express its asset-first
  // centring chain (located assets → else configured pins → else AU/NZ). Keeping
  // this separate from `pins` means the marker-sync effect below still draws
  // exactly the scenario pins regardless of what we centre on.
  const viewportCoords: Coord[] = useMemo(() => focusCoordinates ?? pins, [focusCoordinates, pins]);

  // Effect 1 — Initialise the map once, with an initial viewport derived
  // from the focus coordinates on first render. We deliberately don't re-fit on
  // later renders here: once the user has panned/zoomed we shouldn't yank them
  // back (later async focus arrivals are handled by Effect 2).
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const baseOptions: maplibregl.MapOptions = {
      container: containerRef.current,
      style: STYLE_URL,
    };
    if (viewportCoords.length === 0) {
      baseOptions.bounds = AUSTRALIA_BBOX;
      baseOptions.fitBoundsOptions = { padding: 40 };
    } else if (viewportCoords.length === 1) {
      baseOptions.center = [viewportCoords[0].lng, viewportCoords[0].lat];
      baseOptions.zoom = 13;
    } else {
      const lats = viewportCoords.map((p) => p.lat);
      const lngs = viewportCoords.map((p) => p.lng);
      baseOptions.bounds = [Math.min(...lngs), Math.min(...lats), Math.max(...lngs), Math.max(...lats)];
      baseOptions.fitBoundsOptions = { padding: 60, maxZoom: 14 };
    }

    let map: MlMap;
    try {
      map = new maplibregl.Map(baseOptions);
      map.on("error", (e) => {
        if (e.error?.message?.includes("style") || e.error?.status === 401) {
          map.setStyle(FALLBACK_STYLE);
        }
      });
    } catch {
      map = new maplibregl.Map({ ...baseOptions, style: FALLBACK_STYLE });
    }
    mapRef.current = map;

    // Keep MapLibre's internal size in sync with the container. The map lives
    // in a panel whose width changes with the responsive layout (sidebar
    // collapse, viewport reflow); without a resize on those changes MapLibre's
    // canvas keeps a stale size and `project()` drifts. A ResizeObserver
    // re-syncs on every container size change, including the first real layout
    // pass. (Note: this is NOT what caused the historical click→pin Y offset —
    // that was a marker element positioned `relative` instead of `absolute`,
    // fixed in makeMarkerElement. Kept purely for genuine resizes.)
    const ro =
      typeof ResizeObserver !== "undefined" ?
        new ResizeObserver(() => {
          mapRef.current?.resize();
        })
      : null;
    if (ro && containerRef.current) ro.observe(containerRef.current);

    // Snapshot the marker registry now so cleanup uses the same instance
    // that this effect populated, even if a later render swaps the ref.
    const markers = markersRef.current;
    const handlers = markerClickHandlersRef.current;
    const assetMarkers = assetMarkersRef.current;
    return () => {
      ro?.disconnect();
      map.remove();
      mapRef.current = null;
      markers.clear();
      handlers.clear();
      assetMarkers.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deliberate: only fit once on mount
  }, []);

  // Effect 2 — Auto-fit on later renders, but ONLY in two cases:
  //   (a) zero → non-zero focus transition within the same mount (the user
  //       added their first pin, OR the config page's async asset locations
  //       resolved after mount), gently fly there.
  //   (b) tenant switch — coordinates change wholesale to a new region, re-fit.
  // Subsequent renders that don't match either case leave the viewport alone
  // (so user pan/zoom isn't clobbered). Runs on `viewportCoords`, which equals
  // `pins` for the grid page (unchanged) and the focus chain for the config page.
  const prevViewportRef = useRef<Coord[] | null>(null);
  const prevTenantIdRef = useRef(tenantId);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const tenantChanged = prevTenantIdRef.current !== tenantId;
    const isFirstRender = prevViewportRef.current === null;
    const prevCoords = prevViewportRef.current ?? viewportCoords;
    prevViewportRef.current = viewportCoords;
    prevTenantIdRef.current = tenantId;

    if (isFirstRender && !tenantChanged) return;

    const tenantSwitched = tenantChanged;
    const firstCoordsAppeared = !tenantChanged && prevCoords.length === 0 && viewportCoords.length > 0;
    if (!tenantSwitched && !firstCoordsAppeared) return;

    applyPinViewport(map, viewportCoords, "animate");
  }, [viewportCoords, tenantId]);

  // Effect 2b — Bring the draft pin into view when its *coordinate* changes
  // to a position outside the current viewport. This covers the "type
  // lat/lng" path: without it the operator can paste coordinates and see
  // nothing happen because the marker is rendered off-screen. We only watch
  // the coordinate (not the bearing) so dragging the rotation arrow doesn't
  // perturb the camera, and we no-op when the new coordinate is already
  // inside the visible bounds — typical for click-to-place, where the click
  // is by definition in view.
  const lastFlownDraftRef = useRef<{ lat: number; lng: number } | null>(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !draftPin) {
      lastFlownDraftRef.current = null;
      return;
    }
    const { lat, lng } = draftPin.coordinate;
    const last = lastFlownDraftRef.current;
    if (last && last.lat === lat && last.lng === lng) return;
    lastFlownDraftRef.current = { lat, lng };
    if (map.getBounds().contains([lng, lat])) return;
    map.flyTo({ center: [lng, lat], zoom: Math.max(map.getZoom(), 13), duration: 600 });
  }, [draftPin]);

  // Click-to-place mode toggle
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!onMapClick) {
      map.getCanvas().style.cursor = "";
      return;
    }
    map.getCanvas().style.cursor = "crosshair";
    const handler = (e: maplibregl.MapMouseEvent) => {
      // Ignore the trailing click MapLibre synthesises right after an arrow
      // drag ends — otherwise releasing the rotation handle re-drops the pin at
      // the release point. 250ms comfortably covers the pointerup→click gap
      // without swallowing a genuine subsequent click.
      if (Date.now() - lastArrowDragEndRef.current < 250) return;
      onMapClick(e.lngLat);
    };
    map.on("click", handler);
    return () => {
      map.off("click", handler);
      if (mapRef.current) mapRef.current.getCanvas().style.cursor = "";
    };
  }, [onMapClick]);

  // Effect 4 — Reconcile DOM markers with the current rows. Two passes:
  //   1. for each positioned row: create-or-update its marker, swap stale click handler
  //   2. drop markers for rows that disappeared (e.g. site filter change)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const markers = markersRef.current;
    const handlers = markerClickHandlersRef.current;
    const seen = new Set<string>();

    for (const row of rows) {
      // While the operator is editing this scenario, the live `draftPin`
      // represents it on the map. Skipping it here lets the cleanup pass
      // remove any existing persisted marker so we don't show two pins
      // (saved + draft) for the same scenario.
      if (row.scenarioUuid === suppressedScenarioUuid) continue;
      const c = configByUuid[row.scenarioUuid]?.coordinate;
      if (!c) continue;
      seen.add(row.scenarioUuid);
      const isSelected = selectedScenarioUuid === row.scenarioUuid;
      // Three distinct marker semantics (see also the asset + draft markers):
      //   selected scenario  → brand blue, identical to the ConfigScenarioList
      //                        left-accent bar so the map and list agree on
      //                        "this is the one you're editing";
      //   other scenarios    → neutral slate so they read as background context
      //                        (previously blue, which collided with the list's
      //                        blue selection accent and caused confusion);
      //   asset/camera       → purple (set in the asset-marker effect).
      const isDark = theme.currentTheme === "dark";
      const selectedColour = isDark ? theme.colors.blue[400] : theme.colors.blue[500];
      const otherColour = isDark ? theme.colors.grey[400] : theme.colors.grey[500];
      const colour = isSelected ? selectedColour : otherColour;
      const title = `${row.scenarioName} — ${row.cameraDisplayName}`;
      const direction = configByUuid[row.scenarioUuid]?.direction;
      // The click handler closes over `onScenarioSelect`, which has fresh
      // identity each render — we always want the latest one bound, so the
      // handler is rebuilt + re-attached every pass.
      const onClick = (ev: MouseEvent) => {
        ev.stopPropagation();
        onScenarioSelect(row.scenarioUuid);
      };

      const existing = markers.get(row.scenarioUuid);
      if (existing) {
        // Update in place: position + appearance, then swap the stale click
        // handler (its closure captured the first render's onScenarioSelect).
        existing.setLngLat([c.lng, c.lat]);
        const el = existing.getElement() as HTMLDivElement;
        const dot = el.firstElementChild as HTMLDivElement | null;
        if (dot) dot.style.background = colour;
        el.title = title;
        syncArrow(el, direction, false);
        const prev = handlers.get(row.scenarioUuid);
        if (prev) el.removeEventListener("click", prev);
        el.addEventListener("click", onClick);
        handlers.set(row.scenarioUuid, onClick);
      } else {
        // First time we see this row: create the DOM element and attach.
        const el = makeMarkerElement(colour);
        el.title = title;
        syncArrow(el, direction, false);
        el.addEventListener("click", onClick);
        handlers.set(row.scenarioUuid, onClick);
        const marker = new maplibregl.Marker({ element: el }).setLngLat([c.lng, c.lat]).addTo(map);
        markers.set(row.scenarioUuid, marker);
      }
    }

    // Drop markers for rows that disappeared.
    for (const [uuid, marker] of markers.entries()) {
      if (!seen.has(uuid)) {
        marker.remove();
        markers.delete(uuid);
        handlers.delete(uuid);
      }
    }
  }, [
    rows,
    configByUuid,
    selectedScenarioUuid,
    onScenarioSelect,
    suppressedScenarioUuid,
    theme.currentTheme,
    theme.colors.blue,
    theme.colors.grey,
  ]);

  // Effect 4b — Reconcile asset context markers (config page only; empty for
  // the grid page). Same create-or-update / drop-stale two-pass shape as the
  // scenario markers, keyed by asset id. Depends on a stable JSON signature so
  // it only re-runs when the located-asset set actually changes.
  const assetMarkersKey = useMemo(
    () => (assetMarkers ?? []).map((a) => `${a.id}:${a.coordinate.lat},${a.coordinate.lng}`).join("|"),
    [assetMarkers],
  );
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const markers = assetMarkersRef.current;
    const seen = new Set<string>();

    // Selected asset → purple landmark ("the one you're placing"). Every other
    // located asset recedes to the same neutral grey as non-selected scenario
    // pins, so a multi-asset site isn't a wall of identical purple markers.
    const isDark = theme.currentTheme === "dark";
    const selectedColour = theme.colors.purple[500];
    const otherColour = isDark ? theme.colors.grey[400] : theme.colors.grey[500];
    const colourFor = (assetId: string) => (assetId === selectedAssetId ? selectedColour : otherColour);

    for (const asset of assetMarkers ?? []) {
      seen.add(asset.id);
      const existing = markers.get(asset.id);
      if (existing) {
        existing.setLngLat([asset.coordinate.lng, asset.coordinate.lat]);
        const el = existing.getElement();
        el.title = asset.label;
        // Recolour in place — selection may have changed since creation.
        setAssetMarkerColour(el, colourFor(asset.id));
      } else {
        const el = makeAssetMarkerElement(colourFor(asset.id));
        el.title = asset.label;
        const marker = new maplibregl.Marker({ element: el })
          .setLngLat([asset.coordinate.lng, asset.coordinate.lat])
          .addTo(map);
        markers.set(asset.id, marker);
      }
    }

    for (const [id, marker] of markers.entries()) {
      if (!seen.has(id)) {
        marker.remove();
        markers.delete(id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- assetMarkersKey captures the content; assetMarkers identity is unstable
  }, [assetMarkersKey, selectedAssetId, theme.currentTheme, theme.colors.purple, theme.colors.grey]);

  // Effect 5 — Draft pin lifecycle: create/remove the marker only when the
  // draft pin appears or disappears. Position + arrow rotation updates live
  // in Effect 5b so we don't tear down pointer listeners every drag tick
  // (`draftPin` is a fresh object each render — depending on it directly
  // would re-bind the drag listeners on every move event and stop rotation
  // after one tick).
  //
  // We intentionally do NOT depend on whether a bearing exists. When the
  // marker is draggable the grab handle is always present in the DOM (see
  // `syncArrow`), even before a bearing is set. If we re-created the marker
  // the moment the bearing flips undefined→defined, the very first drag of the
  // neutral handle would tear down the element mid-gesture and strand the
  // in-flight `setPointerCapture`, killing rotation after one tick. Effect 5b
  // handles the undefined→defined visual transition without a rebuild.
  const hasDraftPin = !!draftPin;
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (!hasDraftPin) {
      draftMarkerRef.current?.remove();
      draftMarkerRef.current = null;
      return;
    }

    const initial = draftPinRef.current;
    if (!initial) return;

    const draggable = !!onDraftDirectionChange;
    // The draft pin IS the scenario currently being edited, so it uses the
    // same brand blue as the selected persisted marker and the list accent.
    const isDark = theme.currentTheme === "dark";
    const draftColour = isDark ? theme.colors.blue[400] : theme.colors.blue[500];
    const el = makeDraftMarkerElement(draftColour);
    el.title = "New scenario";
    syncArrow(el, initial.direction, draggable);
    const marker = new maplibregl.Marker({ element: el })
      .setLngLat([initial.coordinate.lng, initial.coordinate.lat])
      .addTo(map);
    draftMarkerRef.current = marker;

    // Cleanup must always remove the marker — every early return below this
    // point composes onto this base teardown, otherwise re-running the effect
    // (e.g. when the pin appears/disappears) would orphan the previous marker.
    const removeMarker = () => {
      marker.remove();
      if (draftMarkerRef.current === marker) draftMarkerRef.current = null;
    };

    if (!draggable) return removeMarker;

    const handle = el.querySelector<SVGCircleElement>(".tf-marker-arrow-handle");
    if (!handle) return removeMarker;

    let dragging = false;
    let originPx: { x: number; y: number } | null = null;

    const compassFromPointer = (clientX: number, clientY: number): number => {
      if (!originPx) return 0;
      const dx = clientX - originPx.x;
      const dy = clientY - originPx.y;
      // Screen y grows downwards; "up" on screen is north (compass 0). Map
      // (dx, dy) so that (0, -1) → 0°, (1, 0) → 90°, (0, 1) → 180°,
      // (-1, 0) → 270°. atan2(dx, -dy) gives exactly that.
      let bearing = (Math.atan2(dx, -dy) * 180) / Math.PI;
      if (bearing < 0) bearing += 360;
      return Math.round(bearing) % 360;
    };

    const refreshOrigin = () => {
      const current = draftPinRef.current;
      if (!current) return;
      const pt = map.project([current.coordinate.lng, current.coordinate.lat]);
      const rect = map.getCanvasContainer().getBoundingClientRect();
      originPx = { x: rect.left + pt.x, y: rect.top + pt.y };
    };

    const onPointerDown = (ev: PointerEvent) => {
      ev.preventDefault();
      ev.stopPropagation();
      dragging = true;
      refreshOrigin();
      handle.setPointerCapture(ev.pointerId);
      handle.style.cursor = "grabbing";
    };

    const onPointerMove = (ev: PointerEvent) => {
      if (!dragging) return;
      ev.preventDefault();
      const bearing = compassFromPointer(ev.clientX, ev.clientY);
      onDraftDirectionChangeRef.current?.(bearing);
    };

    const endDrag = (ev: PointerEvent) => {
      if (!dragging) return;
      dragging = false;
      // Mark the drag end so the map's click handler can ignore the synthetic
      // click MapLibre fires on this same pointer-up (see lastArrowDragEndRef).
      lastArrowDragEndRef.current = Date.now();
      try {
        handle.releasePointerCapture(ev.pointerId);
      } catch {
        // capture may have been released already; ignore
      }
      handle.style.cursor = "grab";
    };

    handle.addEventListener("pointerdown", onPointerDown);
    handle.addEventListener("pointermove", onPointerMove);
    handle.addEventListener("pointerup", endDrag);
    handle.addEventListener("pointercancel", endDrag);
    return () => {
      handle.removeEventListener("pointerdown", onPointerDown);
      handle.removeEventListener("pointermove", onPointerMove);
      handle.removeEventListener("pointerup", endDrag);
      handle.removeEventListener("pointercancel", endDrag);
      removeMarker();
    };
  }, [hasDraftPin, onDraftDirectionChange, theme.currentTheme, theme.colors.blue]);

  // Effect 5b — Sync the draft marker's coordinate + arrow bearing whenever
  // the parent updates the draft (typing in the lat/lng/direction inputs,
  // dragging the rotation handle, etc.). This effect *must not* depend on
  // anything that re-runs Effect 5 — re-creating the marker mid-drag would
  // also drop the in-flight pointer capture.
  useEffect(() => {
    const marker = draftMarkerRef.current;
    if (!marker || !draftPin) return;
    marker.setLngLat([draftPin.coordinate.lng, draftPin.coordinate.lat]);
    const el = marker.getElement() as HTMLDivElement;
    syncArrow(el, draftPin.direction, !!onDraftDirectionChange);
  }, [draftPin, onDraftDirectionChange]);

  return (
    <View
      style={{
        flex: height === undefined ? 1 : undefined,
        height,
        minHeight: 240,
        borderRadius: theme.spacing.md,
        overflow: "hidden",
        borderWidth: 1,
        borderColor: theme.colors.borderCard,
      }}
    >
      <div ref={containerRef} style={{ width: "100%", height: "100%" }} />
    </View>
  );
}
